/**
 * Lock date (BE-31) - Settings › Finance › Lock date, TC admin only.
 *
 * Nothing dated on or before the lock date can be posted or changed. Moving it forward is a plain save; moving it back or
 * clearing it asks for a reason (audited). It can never be before the ledger start. VAT filing never sets it.
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Alert, Skeleton, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { vatReturnService } from '../../services/accounting/vatReturn.service';
import { serverMessage } from '../../utils/apiConvert';
import { formatDate } from '../../utils/dateFormat';

// today stays open (voids, reversals and VAT payments are dated today), so the latest lock date is yesterday
const yesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const LockDateSettings = () => {
  const [saved, setSaved] = useState(null);
  const [lockDate, setLockDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    vatReturnService.getLockDate()
      .then((d) => { setSaved(d); setLockDate(d?.lockDate || ''); })
      .catch((err) => setError(serverMessage(err, 'Failed to load the lock date')))
      .finally(() => setLoading(false));
  }, []);

  const movesBack = Boolean(saved?.lockDate) && (!lockDate || lockDate < saved.lockDate);
  const changed = (lockDate || '') !== (saved?.lockDate || '');
  // filing posts its set-off on the period end, so a lock date covering an unfiled period blocks its filing
  const unfiledCovered = lockDate ? (saved?.unfiledVatPeriods || []).find((p) => p.endDate <= lockDate) : null;
  // depreciation is dated 31 December: a year not run yet is blocked once the lock date covers it
  // months not yet depreciated whose last day the new lock date reaches ('2026-09' → covered by 2026-09-30 or later)
  const monthEnd = (ym) => { const [y, m] = ym.split('-').map(Number); return `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`; };
  const undepreciatedCovered = lockDate ? (saved?.undepreciatedMonths || []).filter((ym) => monthEnd(ym) <= lockDate) : [];
  // pending partner payments must be posted first - the server refuses the save while one is covered
  const pendingCovered = lockDate ? (saved?.pendingPartnerPayments || []).filter((p) => p.occurredOn <= lockDate) : [];

  const save = async (withReason) => {
    setError(null);
    setSuccess(false);
    setSaving(true);
    try {
      const d = await vatReturnService.updateLockDate(lockDate, withReason);
      setSaved(d);
      setLockDate(d?.lockDate || '');
      setReasonOpen(false);
      setReason('');
      setSuccess(true);
    } catch (err) {
      setError(serverMessage(err, 'Failed to save the lock date'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton variant="rounded" height={160} sx={{ maxWidth: 500 }} />;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess(false)}>Lock date saved.</Alert>}
      <Paper variant="outlined" sx={{ p: 2, maxWidth: 500 }}>
        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Lock date</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Nothing dated on or before this date can be added or changed. A void or reversal of an older document is dated
          today, so a closed month stays as it is.
          {saved?.cutoverDate && ` The ledger starts on ${formatDate(saved.cutoverDate)} - nothing before it can change.`}
        </Typography>
        <TextField type="date" size="small" label="Lock date" value={lockDate} onChange={(e) => { setLockDate(e.target.value); setSuccess(false); }}
          InputLabelProps={{ shrink: true }} inputProps={{ min: saved?.cutoverDate || undefined, max: yesterday() }} sx={{ width: 220 }} />
        {unfiledCovered && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            The VAT return for {formatDate(unfiledCovered.startDate)} – {formatDate(unfiledCovered.endDate)} is not filed yet.
            File it before locking {formatDate(unfiledCovered.endDate)}: filing is refused while the lock date covers it.
          </Alert>
        )}
        {undepreciatedCovered.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Depreciation for {undepreciatedCovered.length === 1 ? undepreciatedCovered[0]
              : `${undepreciatedCovered[0]} to ${undepreciatedCovered[undepreciatedCovered.length - 1]}`} is not run yet. Run it
            (Fixed Assets › Run Depreciation) before locking: the run is refused while the lock date covers it.
          </Alert>
        )}
        {pendingCovered.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Post these partner payments (Partner Payments) before locking - they come from the partner and must be on the books:
            {pendingCovered.map((p) => (
              <Box key={p.id} component="div">
                {formatDate(p.occurredOn)} · {p.partner} · AED {Number(p.amount).toFixed(2)}
              </Box>
            ))}
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button variant="contained" startIcon={<Save />} disabled={!changed || saving}
            onClick={() => (movesBack ? setReasonOpen(true) : save(null))}>
            Save
          </Button>
          {saved?.lockDate && (
            <Button disabled={saving} onClick={() => { setLockDate(''); setReasonOpen(true); }}>Clear lock date</Button>
          )}
        </Box>
      </Paper>

      <Dialog open={reasonOpen} onClose={() => !saving && setReasonOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{lockDate ? 'Move the lock date back' : 'Clear the lock date'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {lockDate
              ? `Entries from ${formatDate(lockDate)} onwards can be changed again. The reason is kept in the audit log.`
              : 'Every date after the ledger start can be changed again. The reason is kept in the audit log.'}
          </Typography>
          <TextField autoFocus fullWidth multiline minRows={2} size="small" label="Reason" value={reason}
            onChange={(e) => setReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => { setReasonOpen(false); setLockDate(saved?.lockDate || ''); }}>Cancel</Button>
          <Button variant="contained" disabled={saving || !reason.trim()} onClick={() => save(reason.trim())}>
            {lockDate ? 'Move back' : 'Clear lock date'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LockDateSettings;
